import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  Users,
  GraduationCap,
  Building2,
  ShieldCheck,
  ChevronRight,
} from 'lucide-react'
import supabase from '../../supabaseClient'
import { Alert, AlertDescription } from '../../shadcn/ui/alert'
import Loader from '../Loader'
import OAuthButtons from '../OAuthButtons'
import PortalPasswordLogin from './PortalPasswordLogin'
import PortalLoginDivider from './PortalLoginDivider'
import { signOutClientSession } from '../../utils/authSignOut'
import { readLastPortal, PORTAL_IDS } from '../../utils/lastPortal'
import {
  USERS_LOGIN_SELECT,
  accessiblePortals,
  navigateToSignupIntake,
  routePortalAfterAuth,
} from '../../utils/signupFunnelRedirect'
import { usePortalLoginRedirect } from '../../hooks/usePortalLoginRedirect'
import { readDraft } from '../signup/signupState'
import { peekAcademyLeagueJoinToken } from '../../lib/academyLeagueJoin'
import { peekAcademyJoinId, academyInviteSignInPath } from '../../lib/academyInvite'
import {
  peekCommunityManagerJoinId,
  communityManagerInviteSignInPath,
} from '../../lib/communityInvite'

/**
 * LoginStart — single client-facing sign-in at /login.
 *
 * The user signs in once; we then read their Users row and send them to the
 * workspace they belong to. Users with more than one workspace (e.g. a
 * community manager who is also a coach) get a chooser listing only the
 * workspaces they can actually enter.
 *
 * The per-portal pages (/community/login, /academy/login, /venue-owner/login)
 * still exist for invite and signup flows that rely on their dialogs.
 */

const CAROUSEL_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1200&q=85', alt: 'Football' },
  { src: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1200&q=85', alt: 'Basketball' },
  { src: '/istockphoto-1363976548-612x612.jpg', alt: 'Padel' },
]

const PORTAL_META = {
  community: { icon: Users, title: 'Community portal', fallback: 'Your community' },
  academy: { icon: GraduationCap, title: 'Academy portal', fallback: 'Your academy' },
  venue: { icon: Building2, title: 'Venue portal', fallback: 'Your venue' },
  admin: { icon: ShieldCheck, title: 'Statbox admin', fallback: 'Staff tools' },
}

/* ---------------- Right-column photo carousel ---------------- */

const PortalCarousel = ({ index, onSelect }) => (
  <div className="relative hidden md:block overflow-hidden h-full min-h-0">
    <AnimatePresence initial={false}>
      <motion.img
        key={index}
        src={CAROUSEL_IMAGES[index].src}
        alt={CAROUSEL_IMAGES[index].alt}
        className="absolute inset-0 w-full h-full object-cover object-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
      />
    </AnimatePresence>
    <div className="absolute inset-y-0 left-0 w-32 xl:w-40 bg-gradient-to-r from-black/80 via-black/25 to-transparent pointer-events-none" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
      {CAROUSEL_IMAGES.map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect(i)}
          className={`h-2 rounded-full transition-all ${
            i === index ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
          }`}
          aria-label={`Slide ${i + 1}`}
        />
      ))}
    </div>
  </div>
)

async function fetchCommunityName(communityId) {
  try {
    const { data } = await supabase
      .from('communities')
      .select('name')
      .eq('id', communityId)
      .maybeSingle()
    return data?.name || null
  } catch {
    return null
  }
}

function portalSubtitle(portal, profile, communityName) {
  if (portal === 'community') return communityName
  if (portal === 'academy') return profile?.academies?.name
  if (portal === 'venue') return String(profile?.venue_owner || '').trim()
  return null
}

export default function LoginStart() {
  const navigate = useNavigate()
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [view, setView] = useState('form')
  const [redirecting, setRedirecting] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [account, setAccount] = useState(null)
  const [picking, setPicking] = useState(null)

  useEffect(() => {
    const t = setInterval(
      () => setCarouselIndex((i) => (i + 1) % CAROUSEL_IMAGES.length),
      4500,
    )
    return () => clearInterval(t)
  }, [])

  usePortalLoginRedirect(
    async (session) => {
      const userId = session.user.id
      const { data: profile, error: profileError } = await supabase
        .from('Users')
        .select(USERS_LOGIN_SELECT)
        .eq('user_id', userId)
        .maybeSingle()

      if (profileError) {
        console.error('LoginStart: could not load profile', profileError)
        setError('We could not load your account. Please try again.')
        await signOutClientSession()
        return
      }

      const opts = { blockAutoRedirect: false, userId }

      if (peekAcademyLeagueJoinToken()) {
        await routePortalAfterAuth(navigate, profile, 'academy', opts)
        return
      }

      const portals = accessiblePortals(profile)

      if (portals.length === 1) {
        await routePortalAfterAuth(navigate, profile, portals[0], opts)
        return
      }

      if (portals.length === 0) {
        const academyJoinId = peekAcademyJoinId()
        if (academyJoinId) {
          navigate(academyInviteSignInPath(academyJoinId), { replace: true })
          return
        }
        const communityJoinId = peekCommunityManagerJoinId()
        if (communityJoinId) {
          navigate(communityManagerInviteSignInPath(communityJoinId), { replace: true })
          return
        }
        const draftPersona = readDraft()?.persona
        if (PORTAL_IDS.includes(draftPersona)) {
          await navigateToSignupIntake(navigate, draftPersona)
          return
        }
        setAccount({ userId, email: session.user.email, profile, portals })
        setView('none')
        return
      }

      const communityName = portals.includes('community')
        ? await fetchCommunityName(profile.community_id)
        : null
      const lastPortal = readLastPortal()
      const ordered = lastPortal && portals.includes(lastPortal)
        ? [lastPortal, ...portals.filter((p) => p !== lastPortal)]
        : portals

      setAccount({ userId, email: session.user.email, profile, portals: ordered, communityName, lastPortal })
      setView('choose')
    },
    {
      onStart: () => {
        setRedirecting(true)
        setError('')
      },
      onEnd: () => setRedirecting(false),
      onSignedOut: () => {
        setAccount(null)
        setPicking(null)
        setView('form')
      },
    },
  )

  const handlePick = async (portal) => {
    if (picking || !account) return
    setPicking(portal)
    try {
      const handled = await routePortalAfterAuth(navigate, account.profile, portal, {
        blockAutoRedirect: false,
        userId: account.userId,
      })
      if (!handled) {
        setError('You no longer have access to that workspace.')
        setPicking(null)
      }
    } catch (err) {
      console.error('LoginStart: could not open portal', err)
      setError('Could not open that workspace. Please try again.')
      setPicking(null)
    }
  }

  const handleSwitchAccount = async () => {
    setError('')
    await signOutClientSession()
    setAccount(null)
    setPicking(null)
    setView('form')
  }

  const header = (title, subtitle) => (
    <div className="flex flex-col items-center text-center">
      <div className="flex flex-col items-center gap-3">
        <img src="/Logo_Statbox_Transparent.png" alt="Statbox" className="h-10 w-auto opacity-95" />
        <img src="/logo_white.png" alt="Statbox" className="h-7 w-auto opacity-95" />
      </div>
      <h1 className="mt-6 text-xl font-semibold tracking-tight text-white">{title}</h1>
      {subtitle ? (
        <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-white/45">{subtitle}</p>
      ) : null}
    </div>
  )

  const signedInAs = account?.email ? (
    <p className="text-center text-[13px] text-white/50">
      Signed in as <span className="text-white/80">{account.email}</span>.{' '}
      <button
        type="button"
        onClick={handleSwitchAccount}
        className="font-medium text-[#71cdd9] hover:text-[#9ae4ec] underline-offset-4 hover:underline transition-colors"
      >
        Use a different account
      </button>
    </p>
  ) : null

  const renderForm = () => (
    <>
      <div className="flex flex-col items-center text-center">
        <img src="/Logo_Statbox_Transparent.png" alt="" className="h-10 w-auto opacity-95" />
        <img src="/logo_white.png" alt="Statbox" className="mt-3 h-7 w-auto opacity-95" />
        <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/55">
          Sign in and we'll open the workspace you belong to.
        </p>
      </div>

      <OAuthButtons variant="signin" />

      <PortalLoginDivider />
      <PortalPasswordLogin onError={setError} onSuccessMessage={setSuccessMessage} />

      <div className="flex flex-col gap-2.5 pt-1">
        <p className="text-center text-sm text-white/60">
          New to Statbox?{' '}
          <Link
            to="/signup"
            className="font-semibold text-[#71cdd9] hover:text-[#9ae4ec] underline-offset-4 hover:underline transition-colors"
          >
            Create an account
          </Link>
        </p>
      </div>
    </>
  )

  const renderChooser = () => (
    <>
      {header('Choose a workspace', 'Your account has access to more than one. Pick where to go.')}

      <div className="flex flex-col gap-3">
        {account.portals.map((portal) => {
          const { icon: Icon, title, fallback } = PORTAL_META[portal]
          const subtitle = portalSubtitle(portal, account.profile, account.communityName) || fallback
          const isActive = picking === portal
          return (
            <button
              key={portal}
              type="button"
              onClick={() => handlePick(portal)}
              disabled={!!picking && !isActive}
              className={`group relative w-full text-left rounded-xl border bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-200 px-4 py-3.5 flex items-center gap-3.5 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                isActive
                  ? 'border-[#71cdd9]/60 bg-white/[0.06] shadow-[0_0_30px_-6px_rgba(113,205,217,0.35)]'
                  : 'border-white/10 hover:border-[#71cdd9]/40'
              }`}
            >
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                  isActive
                    ? 'border-[#71cdd9]/45 bg-[#71cdd9]/15'
                    : 'border-white/10 bg-white/[0.04] group-hover:border-[#71cdd9]/30 group-hover:bg-[#71cdd9]/10'
                }`}
              >
                <Icon
                  size={20}
                  strokeWidth={1.7}
                  className={`transition-colors ${
                    isActive ? 'text-[#71cdd9]' : 'text-white/75 group-hover:text-[#71cdd9]'
                  }`}
                  aria-hidden
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold text-white leading-tight truncate">
                    {subtitle}
                  </span>
                  {account.lastPortal === portal && (
                    <span className="shrink-0 rounded-full border border-[#71cdd9]/30 bg-[#71cdd9]/10 px-2 py-0.5 text-[10px] font-medium text-[#9ae4ec]">
                      Last used
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-white/55 leading-snug mt-0.5 truncate">
                  {title}
                </div>
              </div>

              {isActive ? (
                <Loader size={16} />
              ) : (
                <ChevronRight
                  size={16}
                  className="shrink-0 text-white/25 group-hover:text-white/60 transition-colors"
                  aria-hidden
                />
              )}
            </button>
          )
        })}
      </div>

      {signedInAs}
    </>
  )

  const renderNoWorkspace = () => (
    <>
      {header(
        'No workspace yet',
        "This account isn't linked to a community, academy or venue on Statbox.",
      )}

      <button
        type="button"
        onClick={() => navigate('/signup')}
        className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-[var(--signup-accent)] text-sm font-medium text-white transition hover:bg-[var(--signup-accent-hover)]"
      >
        Set up a workspace
      </button>

      <p className="text-center text-[13px] text-white/50">
        Invited by someone? Open the invite link they sent you to join their workspace.
      </p>

      {signedInAs}
    </>
  )

  return (
    <div className="grid min-h-svh min-h-[100dvh] md:grid-cols-2 bg-black text-white">
      <div className="flex min-h-0 flex-col gap-2 overflow-y-auto px-4 pb-4 pt-2 md:p-10 md:pt-8">
        <div className="flex justify-start">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>

        <div className="flex w-full flex-1 items-start justify-center md:items-center">
          <div className="w-full max-w-sm">
            {redirecting ? (
              <div className="flex flex-col items-center py-24 text-center">
                <Loader size={32} />
                <p className="mt-4 text-[13px] text-white/50">Signing you in…</p>
              </div>
            ) : (
              <motion.div
                key={view}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col gap-4"
              >
                {view === 'choose' && account && renderChooser()}
                {view === 'none' && account && renderNoWorkspace()}
                {view === 'form' && renderForm()}

                <AnimatePresence>
                  {(error || successMessage) && (
                    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                      <Alert variant={error ? 'destructive' : 'default'} className="rounded-lg border">
                        <AlertDescription className="text-sm">{error || successMessage}</AlertDescription>
                      </Alert>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <PortalCarousel index={carouselIndex} onSelect={setCarouselIndex} />
    </div>
  )
}
